import * as angular from 'angular';
import './styling.scss';
import * as template from './template.html';

import StringMask = require('string-mask');

import * as Panel from 'common/source/components/panel';
import { NavigationService } from 'services/navigation';
import { CurrentUserService } from 'services/currentUser';
import { AuthenticationService } from 'services/authentication';
import { LoggingService } from 'services/logging';
import { extractTenDigitsFromPhoneNumber, PHONE_NUMBER_PATTERN } from 'common/source/utilities/strings';
import * as auth0 from 'auth0-js';

import * as _ from 'lodash';

const module = angular.module('login-panel', [Panel.module.name]);

enum LoginMode {
    Email,
    Sms
}

class LoginPanelController {
    private $timeout: angular.ITimeoutService;
    private $element: angular.IAugmentedJQuery;
    private $scope: angular.IScope;
    private authenticationService: AuthenticationService;
    private loggingService: LoggingService;

    /* bindings */
    private attemptedLogin?: boolean;
    private loginAttemptFailed?: boolean;
    private loginAttemptSucceeded?: boolean;
    private notACustomer?: boolean;
    private unknownLoginError?: boolean;

    private inputPlaceholder: string;
    private phoneOrEmail?: string;
    private accessCode?: string;

    private phoneInputPlaceholder?: string;

    private webAuth: auth0.WebAuth;

    private isInputFocused: boolean;

    private accessCodeSent?: boolean;
    private invalidPhoneOrEmail?: boolean;

    private loginMode?: LoginMode;
    private requestedAccessCode?: boolean;

    private accessCodeResent?: boolean;

    private unknownError?: boolean;
    private invalidPhoneNumber?: boolean;
    private invalidEmail?: boolean;
    private unknownPhoneNumber?: boolean;
    private unknownEmail?: boolean;
    private invalidAccessCode?: boolean;
    private authenticatedUserIsNotACustomer?: boolean;

    private rememberMe?: boolean;

    // tslint:disable-next-line:max-line-length
    private emailPattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    private nonEmailPattern = /^[-+\(\)0-9]+$/;
    private invalidEmailPattern = /@|\.|[a-zA-Z]/;

    private phoneNumberMask = new StringMask('000-000-0000');

    // we use this to track if we're expecting a response from auth0, to detect communication errors
    private expectedResponseTimeout: angular.IPromise<any> | undefined;

    static $inject = [
        '$scope',
        '$element',
        'navigationService',
        'currentUserService',
        '$timeout',
        'authenticationService',
        'loggingService'
    ];

    constructor(
        $scope: angular.IScope,
        $element: angular.IAugmentedJQuery,
        navigationService: NavigationService,
        currentUserService: CurrentUserService,
        $timeout: angular.ITimeoutService,
        authenticationService: AuthenticationService,
        loggingService: LoggingService
    ) {
        this.$timeout = $timeout;
        this.$element = $element;
        this.authenticationService = authenticationService;
        this.loggingService = loggingService;
        this.$scope = $scope;

        this.webAuth = {} as auth0.WebAuth; // initialized in the following function
        this.initializeWebAuth();

        this.inputPlaceholder = 'Enter phone or email';
        // originally there were other cases where the placeholder changed, and there may be again

        this.isInputFocused = true; // prevent initial animation

        this.$timeout(() => {
            // timeout is so the UI has a chance to render the new input box
            this.focusMainInput();
        });
    }

    $onInit() {
        if (this.notACustomer) {
            this.authenticatedUserIsNotACustomer = true;
            this.accessCodeSent = false;
        } else if (this.loginAttemptFailed) {
            this.invalidAccessCode = true;
        } else if (this.unknownLoginError) {
            this.unknownError = true;
        }
    }

    getMainInput() {
        const mainInputs = this.$element[0].querySelectorAll('.main-input input') as NodeListOf<HTMLInputElement>;

        return _.find(mainInputs, (input) => {
            return input.offsetWidth !== 0 && input.offsetHeight !== 0;
        });
    }

    focusMainInput() {
        const mainInput = this.getMainInput();

        if (!mainInput) {
            return;
        }

        mainInput.focus();

        if (angular.element(mainInput).hasClass('mobile-only')) {
            this.onMobileInputFocus();
        } else {
            this.onDesktopInputFocus();
        }
    }

    onMobileInputBlur() {
        this.isInputFocused = false;
        this.phoneInputPlaceholder = '';
    }

    onMobileInputFocus() {
        this.isInputFocused = true;
        this.phoneInputPlaceholder = '(123) 456-7890';
    }

    onDesktopInputBlur() {
        this.isInputFocused = false;
        this.phoneInputPlaceholder = '';
    }

    onDesktopInputFocus() {
        this.isInputFocused = true;
        this.phoneInputPlaceholder = '';
    }

    phoneOrEmailKeypress($event: KeyboardEvent) {
        if ($event.which === 13) {
            this.requestCode();
        }
    }

    accessCodeKeypress($event: KeyboardEvent) {
        if ($event.which === 13) {
            this.loginWithCode();
        }
    }

    formatInput() {
        const input = angular.element(this.$element[0].querySelector('.main-input input') as HTMLInputElement);

        const tenDigitNumber = extractTenDigitsFromPhoneNumber(this.phoneOrEmail);

        if (tenDigitNumber) {
            input.val(this.phoneNumberMask.apply(tenDigitNumber));
        }
    }

    getFormattedPhoneOrEmail() {
        const tenDigitNumber = extractTenDigitsFromPhoneNumber(this.phoneOrEmail);

        if (tenDigitNumber) {
            return StringMask.apply(tenDigitNumber, '(000) 000-0000');
        } else {
            return this.phoneOrEmail;
        }
    }

    initializeWebAuth() {
        const auth0Config = this.authenticationService.getAuth0Config();

        this.webAuth = new auth0.WebAuth({
            domain: auth0Config.domain,
            clientID: auth0Config.clientID,
            redirectUri: auth0Config.redirectUri,
            responseType: 'token id_token'
        });
    }

    isValidPhoneOrEmail() {
        if (!this.phoneOrEmail) {
            return false;
        }

        return PHONE_NUMBER_PATTERN.test(this.phoneOrEmail) || this.emailPattern.test(this.phoneOrEmail);
    }

    requestCode() {
        this.resetLogin();

        if (!this.phoneOrEmail) {
            this.invalidPhoneOrEmail = true;
            return;
        }

        if (PHONE_NUMBER_PATTERN.test(this.phoneOrEmail)) {
            this.loginMode = LoginMode.Sms;
        } else if (this.emailPattern.test(this.phoneOrEmail)) {
            this.loginMode = LoginMode.Email;
        } else if (this.nonEmailPattern.test(this.phoneOrEmail)) {
            this.invalidPhoneNumber = true;
            return;
        } else if (this.invalidEmailPattern.test(this.phoneOrEmail)) {
            this.loggingService.log(
                'invalid email pattern',
                this.invalidEmailPattern.test(this.phoneOrEmail),
                this.phoneOrEmail
            );
            this.invalidEmail = true;
            return;
        } else {
            this.invalidPhoneOrEmail = true;
            return;
        }
        this.sendCode();
    }

    sendCode(resending: boolean = false) {
        if (!resending) {
            this.resetLogin();
        }

        let passwordlessOptions;
        if (this.loginMode === LoginMode.Email) {
            passwordlessOptions = {
                send: 'code',
                connection: 'email',
                email: this.phoneOrEmail
            };
        } else {
            const tenDigitNumber = extractTenDigitsFromPhoneNumber(this.phoneOrEmail);
            if (!tenDigitNumber) {
                this.invalidPhoneNumber = true;
                return;
            }

            passwordlessOptions = {
                connection: 'sms',
                send: 'code',
                phoneNumber: '+1' + tenDigitNumber
            };
        }

        this.webAuth.passwordlessStart(passwordlessOptions, (error, response) => {
            this.requestedAccessCode = true;

            if (error) {
                if (error.code === 'bad.connection' || error.description === 'Public signup is disabled') {
                    this.unknownPhoneNumber = this.loginMode === LoginMode.Sms;
                    this.unknownEmail = this.loginMode === LoginMode.Email;
                } else {
                    this.invalidPhoneNumber = this.loginMode === LoginMode.Sms;
                    this.invalidEmail = this.loginMode === LoginMode.Email;
                }
            } else {
                this.accessCodeSent = true;

                this.$timeout(() => {
                    // timeout is so the UI has a chance to render the new input box
                    this.focusMainInput();
                });
            }

            this.$scope.$apply();
        });
    }

    loginWithCode() {
        const $ctrl = this;

        // we need to cast webAuth as "any" because the auth0 sdk @types are not up to date with recent additions
        let loginOptions;
        if (this.loginMode === LoginMode.Email) {
            loginOptions = {
                connection: 'email',
                verificationCode: this.accessCode,
                email: this.phoneOrEmail
            };
        } else {
            const tenDigitNumber = '+1' + extractTenDigitsFromPhoneNumber(this.phoneOrEmail);
            loginOptions = {
                connection: 'sms',
                verificationCode: this.accessCode,
                phoneNumber: tenDigitNumber
            };
        }

        this.authenticationService.setSessionCookie('remember', this.rememberMe ? '1' : '');

        (this.webAuth as any).passwordlessLogin(loginOptions, function(error: any, response: any) {
            if (error) {
                // error.code === 'access_denied' is the usual SMS failure code
                $ctrl.invalidAccessCode = true;
            } else {
                // if login was successful, the user will be redirected to the login page (effectively a refresh)
                // again, which will check the url hash
            }
        });
    }

    resendAccessCode() {
        this.accessCodeResent = true;
        this.sendCode(true);
    }

    resetLogin() {
        this.accessCodeSent = false;
        this.accessCodeResent = false;
        this.authenticatedUserIsNotACustomer = false;
        this.invalidAccessCode = false;
        this.invalidEmail = false;
        this.invalidPhoneNumber = false;
        this.invalidPhoneOrEmail = false;
        this.unknownEmail = false;
        this.unknownPhoneNumber = false;
        this.requestedAccessCode = false;
    }

    resetLoginAndInput() {
        this.resetLogin();

        this.phoneOrEmail = '';

        this.$timeout(() => {
            this.focusMainInput();
        });
    }

    expectResponse() {
        this.$timeout.cancel(this.expectedResponseTimeout);

        this.expectedResponseTimeout = this.$timeout(
            () => {
                this.unknownError = true;
            },
            10000,
            true
        );
    }

    private receivedExpectedResponse() {
        this.$timeout.cancel(this.expectedResponseTimeout);
    }

    hasErrors() {
        return !!(
            this.unknownEmail ||
            this.unknownPhoneNumber ||
            this.authenticatedUserIsNotACustomer ||
            this.invalidAccessCode ||
            this.invalidEmail ||
            this.invalidPhoneNumber ||
            this.invalidPhoneOrEmail
        );
    }
}

module.component('loginPanel', {
    template: template,
    transclude: true,
    bindings: {
        attemptedLogin: '<?',
        loginAttemptSucceeded: '<?',
        loginAttemptFailed: '<?',
        notACustomer: '<?',
        unknownLoginError: '<?'
    },
    controller: LoginPanelController
});

export { module, LoginMode };
